import dayjs from 'dayjs';

const icelandicOrder = [
    '',
    'A',
    'Á',
    'B',
    'C',
    'D',
    'Ð',
    'E',
    'É',
    'F',
    'G',
    'H',
    'I',
    'Í',
    'J',
    'K',
    'L',
    'M',
    'N',
    'O',
    'Ó',
    'P',
    'Q',
    'R',
    'S',
    'T',
    'U',
    'Ú',
    'V',
    'W',
    'X',
    'Y',
    'Ý',
    'Z',
    'Þ',
    'Æ',
    'Ö',
];

const icelandicOrderMap = icelandicOrder.reduce((map, char, index) => {
    map[char] = index;
    return map;
}, {});

const getCharOrder = (char) => {
    return icelandicOrderMap[char.toUpperCase()] || char.charCodeAt(0);
};

const compareIcelandicStrings = (a, b) => {
    const aChars = a.split('');
    const bChars = b.split('');

    for (let i = 0; i < Math.min(aChars.length, bChars.length); i++) {
        const aOrder = getCharOrder(aChars[i]);
        const bOrder = getCharOrder(bChars[i]);

        if (aOrder !== bOrder) {
            return aOrder - bOrder;
        }
    }

    return aChars.length - bChars.length;
};

export const DEFAULT_EVENT_ICON = '/img/default_event.png';
export const DEFAULT_EVENT_NAME = 'Reevent';

export const REPEATING_EVENT = 0;
export const SINGLE_EVENT = 1;

export const EMOTION_LIKE = 0;
export const EMOTION_HEART = 1;
export const EMOTION_HAPPY = 2;
export const EMOTION_SAD = 3;

export function backendUrl() {
    const url = process.env.REACT_APP_BACKEND_URL;
    const port = process.env.REACT_APP_BACKEND_PORT;
    if (url !== undefined && port !== undefined) {
        return url + ':' + port;
    }
    console.log(
        'WARNING: .env is missing either REACT_APP_BACKEND_URL or ' +
            'REACT_APP_BACKEND_PORT (or both).'
    );
    return 'http://localhost:3001';
}

export function dayNumberToDayName(number) {
    switch (number) {
        case 0:
            return 'Sunday';
        case 1:
            return 'Monday';
        case 2:
            return 'Tuesday';
        case 3:
            return 'Wednesday';
        case 4:
            return 'Thursday';
        case 5:
            return 'Friday';
        case 6:
            return 'Saturday';
        case 7:
            return 'Sunday';
        default:
            throw new Error('Invalid day number ' + number);
    }
}

export function sortByName(dataArray) {
    return [...dataArray].sort((a, b) =>
        compareIcelandicStrings(a.name, b.name)
    );
}

// This sorting function ensures that participants are listed by:
//  - The confirmed ones are at the top.
//  - The ones who declined are listed next.
//  - Those who haven't answered are listed at the bottom.
//  - Internally, each group is sorted alphabetically.
export function sortParticipantsByAttendance(participants) {
    const LEFT_FIRST = -1;
    const RIGHT_FIRST = 1;
    return [...participants].sort((left, right) => {
        if (left.confirmed === true) {
            if (right.confirmed !== true) return LEFT_FIRST;
            // Fall through to name comparison (both a and b are attending).
        } else if (left.confirmed === false) {
            if (right.confirmed === true) return RIGHT_FIRST;
            if (right.confirmed !== false) return LEFT_FIRST;
            // Fall through to name comparison (neither a nor b are attending).
        } else {
            // A is neither true nor false, so it must be undecided (null).
            if (right.confirmed !== null) return RIGHT_FIRST;
            // Fall through to name comparison (neither a nor b have responded).
        }
        return compareIcelandicStrings(left.name, right.name);
    });
}

// This sorting function ensures that participants are listed by when they
// replied to the event (most recent responses at the top). Internally, each
// group is sorted alphabetically.
export function sortByTime(dataArray) {
    const LEFT_FIRST = -1;
    const RIGHT_FIRST = 1;
    return [...dataArray].sort((left, right) => {
        if (left.confirmed === null) {
            if (right.confirmed !== null) {
                return RIGHT_FIRST;
            }
        } else {
            if (right.confirmed === null) {
                return LEFT_FIRST;
            }
        }

        if (left.timestamp !== right.timestamp) {
            const dateA = new Date(left.timestamp);
            const dateB = new Date(right.timestamp);
            return dateB - dateA;
        }

        return compareIcelandicStrings(left.name, right.name);
    });
}

// Helper function to calculate age from birthdate
const calculateAgeFromBirthdate = (birthdate) => {
    if (!birthdate) return null;

    const birthDate = new Date(birthdate);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }

    return age;
};

// Helper function to calculate age from Icelandic kennitala
const calculateAgeFromKennitala = (kennitala) => {
    if (!kennitala || kennitala.length < 6) return null;

    const day = parseInt(kennitala.substring(0, 2), 10);
    const month = parseInt(kennitala.substring(2, 4), 10);
    let year = parseInt(kennitala.substring(4, 6), 10);

    const centuryDigit = kennitala.length >= 10 ? kennitala[9] : '0';
    if (centuryDigit === '9' || centuryDigit === '0') {
        year += 1900;
    } else {
        year += 2000;
    }

    const birthDate = new Date(year, month - 1, day);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }

    return age;
};

// Sort participants/attendances by age (youngest to oldest)
// Works with both participant objects and attendance objects (which have nested participant)
export function sortByAge(dataArray) {
    return [...dataArray].sort((a, b) => {
        // Handle attendance objects with nested participant
        const participantA = a.participant || a;
        const participantB = b.participant || b;

        // Try birthdate first, then fall back to SSN
        let ageA = calculateAgeFromBirthdate(participantA.birthdate);
        if (ageA === null) {
            ageA = calculateAgeFromKennitala(participantA.ssn);
        }

        let ageB = calculateAgeFromBirthdate(participantB.birthdate);
        if (ageB === null) {
            ageB = calculateAgeFromKennitala(participantB.ssn);
        }

        // If both have valid ages, sort by age (youngest first)
        if (ageA !== null && ageB !== null) {
            if (ageA !== ageB) {
                return ageA - ageB; // Ascending order (youngest first)
            }
            // If same age, sort alphabetically
            return compareIcelandicStrings(a.name, b.name);
        }

        // If only one has a valid age, put the one with age first
        if (ageA !== null) return -1;
        if (ageB !== null) return 1;

        // If neither has a valid age, sort alphabetically
        return compareIcelandicStrings(a.name, b.name);
    });
}

export function getAttendanceCount(participants) {
    return participants.reduce((accumulator, participant) => {
        if (
            participant.confirmed === true || //TODO - some inconsistency in data usage. Should be 1/0
            participant.confirmed === 1 ||
            participant.confirmed === '1'
        ) {
            return accumulator + 1;
        }
        return accumulator;
    }, 0);
}

export function getAttendancePercentage(attendance, minParticipantCount) {
    let attendancePercentage = (attendance / minParticipantCount) * 100;
    return attendancePercentage > 100 ? 100 : attendancePercentage;
}

// Convert Icelandic SSN to birthday and return as YYYY-MM-DD.
export function obtainBirthdateFromSsn(ssn) {
    if (!ssn || ssn.length !== 10 || ssn === '0000000000') {
        return null;
    }
    const birthdate =
        (ssn[9] === '0' ? '20' : '19') +
        ssn[4] +
        ssn[5] +
        '-' +
        ssn[2] +
        ssn[3] +
        '-' +
        ssn[0] +
        ssn[1];
    return birthdate;
}

export function saveLastCreatedUuid(uuid) {
    localStorage.setItem('lastCreatedUuid', uuid);
}

export function ensureNonNegativeAndForwardTo(value, recipientFunction) {
    // Ensure the input is a non-negative integer
    let newValue = value === '' ? 0 : parseInt(value, 10);
    newValue = Math.max(0, newValue);
    // Pass the sanitized input to the function supplied.
    recipientFunction(isNaN(newValue) ? 0 : newValue);
}

export function isTestUser(userEmail) {
    // The date and time pickers support showing a confirmation button,
    // which are annoying for humans, but help a lot in testing
    // (prevents the test from having to click a random field to get the
    // dropdowns to disappear after text input), so we set these buttons
    // to show only for our test users.
    return (
        userEmail === 'testuser@example.com' ||
        userEmail === 'testuser_data@example.com' ||
        userEmail === 'testuser_main@example.com'
    );
}

export function dateMaybeWithYear(displayDate, locale) {
    const date = dayjs(displayDate);
    if (date.year() === dayjs().year())
        return date.locale(locale).format('D. MMM');
    else return date.locale(locale).format('D. MMM YYYY');
}

// A function to use with the DatePicker, which restricts the users to
// select only today or days in the future.
export function disableDateYesterday(date) {
    return date && date <= dayjs().add(-1, 'day');
}